<script setup>
import { computed, ref, watch } from 'vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  usageCsv,
  usageMonthOptions,
  USAGE_TIMEZONE,
  usageCount,
  maskUsageCount,
  formatUsageRate,
  maskUsageRate,
  parseUsageRate,
} from './messageUsageHelpers';

const props = defineProps({
  inboxId: { type: [Number, String], default: null },
  accountOverview: { type: Boolean, default: false },
});
const months = usageMonthOptions();
const month = ref(months.find(option => option.current).value);
const report = ref(null);
const selectedId = ref(Number(props.inboxId));
const loading = ref(false);
const saving = ref(false);
const error = ref('');
const notice = ref('');
const showSettings = ref(false);
const settingsDialogRef = ref(null);
const settingsError = ref('');
const settingsTarget = ref(null);
const draft = ref({ monthly_allowance: '1.000', rates: {} });
let requestId = 0;
const categories = {
  service: 'Serviço',
  utility: 'Utilidade',
  authentication: 'Autenticação',
  marketing: 'Marketing',
  unknown: 'Sem categoria',
};
const number = computed(() =>
  report.value?.numbers.find(row => row.inbox_id === selectedId.value)
);
const numbers = computed(
  () =>
    report.value?.numbers.map(row => ({
      value: row.inbox_id,
      label: `${row.name} · ${row.phone_number}`,
    })) || []
);
const daily = computed(() =>
  Object.entries(number.value?.daily || {}).sort(([a], [b]) =>
    a.localeCompare(b)
  )
);
const maxDaily = computed(() =>
  Math.max(1, ...daily.value.map(([, count]) => count))
);
const money = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value
  );
const dateTime = value =>
  value
    ? new Date(value).toLocaleString('pt-BR', { timeZone: USAGE_TIMEZONE })
    : 'Ainda sem eventos';

const decimalCount = value =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(
    Number(value) || 0
  );
const averageVolume = computed(() =>
  decimalCount(number.value?.volume_history?.average)
);
const metrics = computed(() =>
  number.value
    ? [
        {
          label: 'Mensagens entregues',
          value: usageCount(number.value.delivered),
          icon: 'i-lucide-message-square-check',
          description: 'Entregas únicas registradas no período',
        },
        {
          label: 'Com cobrança Meta',
          value: usageCount(number.value.billable),
          icon: 'i-lucide-receipt',
          description: 'Classificadas como cobradas pela Meta',
        },
        {
          label: 'Sem cobrança Meta',
          value: usageCount(number.value.free),
          icon: 'i-lucide-circle-check',
          description: 'Classificadas como gratuitas pela Meta',
        },
        {
          label: 'Custo estimado parcial',
          value: money(number.value.estimated_cost_brl || 0),
          icon: 'i-lucide-wallet',
          description: `${usageCount(number.value.unpriced)} cobranças sem tarifa aplicável`,
          highlight: true,
        },
      ]
    : []
);

async function load() {
  requestId += 1;
  const current = requestId;
  loading.value = true;
  error.value = '';
  report.value = null;
  if (showSettings.value) settingsDialogRef.value?.close();
  showSettings.value = false;
  try {
    const { data } = props.accountOverview
      ? await InboxHealthAPI.getAccountMessageUsage(month.value)
      : await InboxHealthAPI.getMessageUsage(props.inboxId, month.value);
    if (current !== requestId) return;
    report.value = data;
    if (!data.numbers.some(row => row.inbox_id === selectedId.value))
      selectedId.value = data.numbers[0]?.inbox_id;
  } catch {
    if (current === requestId)
      error.value =
        'Não foi possível carregar o consumo. Este relatório exige acesso de administrador.';
  } finally {
    if (current === requestId) loading.value = false;
  }
}
function editSettings() {
  const settings = number.value.settings;
  draft.value = {
    monthly_allowance: usageCount(settings.monthly_allowance),
    effective_on: settings.effective_on,
    rates: Object.fromEntries(
      Object.entries(settings.rates).map(([key, value]) => [
        key,
        formatUsageRate(value),
      ])
    ),
  };
  notice.value = '';
  settingsError.value = '';
  settingsTarget.value = {
    id: number.value.inbox_id,
    name: number.value.name,
    phone: number.value.phone_number,
  };
  showSettings.value = true;
  settingsDialogRef.value?.open();
}
async function saveSettings() {
  if (saving.value || !settingsTarget.value) return;
  saving.value = true;
  settingsError.value = '';
  const settings = {
    ...draft.value,
    monthly_allowance: Number(draft.value.monthly_allowance.replace(/\./g, '')),
    rates: Object.fromEntries(
      Object.entries(draft.value.rates)
        .filter(([, rate]) => rate !== '')
        .map(([key, rate]) => [key, parseUsageRate(rate)])
    ),
  };
  try {
    await InboxHealthAPI.updateMessageUsageSettings(
      settingsTarget.value.id,
      settings
    );
    settingsDialogRef.value?.close();
    await load();
    notice.value = 'Parâmetros salvos para este número.';
  } catch {
    settingsError.value =
      'Não foi possível salvar. Confira a meta mensal e as tarifas (valores entre 0 e 1.000).';
  } finally {
    saving.value = false;
  }
}
function download() {
  const url = URL.createObjectURL(
    new Blob(['\uFEFF', usageCsv(report.value)], {
      type: 'text/csv;charset=utf-8;',
    })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = `consumo-meta-${month.value}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
watch(
  [() => props.inboxId, () => props.accountOverview, month],
  ([id], previous) => {
    if (!previous || id !== previous[0]) selectedId.value = Number(id);
    load();
  },
  { immediate: true }
);
watch(selectedId, () => {
  showSettings.value = false;
  notice.value = '';
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="space-y-5 text-n-slate-12">
    <header
      class="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-5"
    >
      <div class="flex items-center gap-3">
        <span
          class="grid size-12 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <span class="i-brand-meta size-7" aria-hidden="true" />
        </span>
        <div>
          <h2 class="text-xl font-semibold">Consumo Meta</h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{
              accountOverview
                ? 'Entregas e custos estimados da sua empresa.'
                : 'Consumo e estimativa somente desta caixa.'
            }}
          </p>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <ComboBox
          v-model="month"
          :options="months"
          :disabled="saving"
          class="min-w-44"
          aria-label="Mês do relatório"
        />
        <Button
          label="Atualizar"
          icon="i-lucide-refresh-cw"
          sm
          ghost
          :disabled="loading || saving"
          @click="load"
        />
        <Button
          v-if="report"
          label="Exportar CSV"
          icon="i-lucide-download"
          sm
          faded
          @click="download"
        />
      </div>
    </header>
    <p
      v-if="error"
      role="alert"
      class="rounded-xl bg-n-ruby-3 p-4 text-sm text-n-ruby-11"
    >
      {{ error }}
    </p>
    <p
      v-if="notice"
      role="status"
      class="rounded-xl bg-n-teal-3 p-4 text-sm text-n-teal-11"
    >
      {{ notice }}
    </p>
    <div
      v-if="loading"
      role="status"
      class="rounded-2xl border border-n-weak bg-n-solid-1 p-8 text-center text-sm text-n-slate-11"
    >
      Carregando consumo…
    </div>
    <template v-if="report">
      <div
        v-if="!report.numbers.length"
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-8 text-center"
      >
        <span class="i-lucide-message-square size-8 text-n-slate-10" />
        <p class="mt-3 text-sm text-n-slate-11">
          Nenhum número WhatsApp Cloud disponível neste relatório.
        </p>
      </div>
      <template v-if="number">
        <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex flex-wrap items-end justify-between gap-3">
            <div class="min-w-0 flex-1">
              <p class="mb-2 text-xs font-medium text-n-slate-11">
                CONEXÃO EM ANÁLISE
              </p>
              <ComboBox
                v-if="accountOverview"
                v-model="selectedId"
                :options="numbers"
                :disabled="saving"
                class="w-full sm:max-w-xl"
                placeholder="Selecione o número"
              />
              <p v-else class="font-medium">
                {{ number.name }}
                <span class="text-sm font-normal text-n-slate-11">
                  · {{ number.phone_number }}
                </span>
              </p>
            </div>
            <Button
              label="Configurar estimativa"
              icon="i-lucide-settings-2"
              sm
              faded
              :disabled="saving"
              @click="editSettings"
            />
          </div>
          <div
            class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-n-slate-11"
          >
            <span class="inline-flex items-center gap-1.5">
              <span class="i-lucide-clock-3 size-3.5" />
              Horário de São Paulo
            </span>
            <span>
              Primeiro registro: {{ dateTime(number.first_observed_at) }}
            </span>
          </div>
        </section>
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div
            v-for="metric in metrics"
            :key="metric.label"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-5"
          >
            <div class="flex items-center justify-between gap-2">
              <p class="text-sm text-n-slate-11">{{ metric.label }}</p>
              <span
                class="size-5 shrink-0 text-n-slate-10"
                :class="[metric.icon]"
                aria-hidden="true"
              />
            </div>
            <p
              class="mt-4 text-2xl font-semibold tabular-nums tracking-tight"
              :class="metric.highlight ? 'text-n-brand' : 'text-n-slate-12'"
            >
              {{ metric.value }}
            </p>
            <p class="mt-2 text-xs text-n-slate-11">{{ metric.description }}</p>
          </div>
        </div>
        <div
          v-if="
            number.unknown_pricing || number.read_inferred || number.unpriced
          "
          class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
        >
          <span class="i-lucide-info mt-0.5 size-4 shrink-0 text-n-amber-11" />
          <div>
            <p class="font-medium text-n-slate-12">Dados em acompanhamento</p>
            <p class="mt-1">
              {{ usageCount(number.unknown_pricing) }} entregas sem
              classificação de cobrança ·
              {{ usageCount(number.read_inferred) }} entregas inferidas da
              leitura · {{ usageCount(number.unpriced) }} cobranças sem tarifa
              aplicável.
            </p>
            <p class="mt-1 text-xs">
              Os totais são atualizados quando novos eventos chegam. Valores sem
              tarifa ficam fora da estimativa.
            </p>
          </div>
        </div>
        <div class="grid gap-4 lg:grid-cols-2">
          <section
            v-if="number.volume_history"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-5"
            data-testid="message-volume-history"
          >
            <div class="flex items-center gap-2">
              <span
                class="i-lucide-chart-no-axes-combined size-4 text-n-brand"
              />
              <h3 class="font-medium">Média mensal</h3>
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Três meses fechados anteriores ao período selecionado, sem incluir
              o mês atual.
            </p>
            <p class="my-5 text-3xl font-semibold tabular-nums">
              {{ averageVolume }}
              <span class="text-sm font-normal text-n-slate-11">
                mensagens/mês
              </span>
            </p>
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="period in number.volume_history.months"
                :key="period.month"
                class="rounded-xl bg-n-alpha-1 p-3"
              >
                <p class="text-xs text-n-slate-11">
                  {{ period.month.split('-').reverse().join('/') }}
                </p>
                <p class="mt-2 font-semibold tabular-nums">
                  {{ usageCount(period.delivered) }}
                </p>
              </div>
            </div>
            <p class="mt-3 text-xs text-n-slate-11">
              Meses sem registros entram como zero. Histórico anterior à coleta
              não foi reconstituído.
            </p>
          </section>
          <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
            <div class="flex items-center justify-between gap-2">
              <h3 class="flex items-center gap-2 font-medium">
                <span class="i-lucide-target size-4 text-n-brand" />
                Meta mensal de serviço
              </h3>
              <span
                class="rounded-md bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-11"
              >
                {{
                  number.allowance == null
                    ? 'Ainda não vigente'
                    : 'Planejamento'
                }}
              </span>
            </div>
            <p class="my-5 text-3xl font-semibold tabular-nums">
              {{ usageCount(number.service) }}
              <span class="text-sm font-normal text-n-slate-11">
                /
                {{
                  usageCount(
                    number.allowance ?? number.settings.monthly_allowance
                  )
                }}
                mensagens
              </span>
            </p>
            <template v-if="number.allowance != null">
              <progress
                class="h-2 w-full accent-current text-n-brand"
                :value="Math.min(number.service, number.allowance)"
                :max="number.allowance || 1"
                aria-label="Consumo da meta mensal de serviço"
              />
              <p class="mt-3 text-sm text-n-slate-11">
                {{ decimalCount(number.allowance_percent) }}% atingido ·
                {{ usageCount(number.remaining) }} restantes ·
                {{ usageCount(number.excess) }} acima da meta.
              </p>
            </template>
            <p class="mt-3 text-xs text-n-slate-11">
              Referência por número a partir de
              {{ number.settings.effective_on.split('-').reverse().join('/') }}.
              Exclui entradas gratuitas identificadas pela Meta. Atingir a meta
              não determina cobrança nem bloqueia mensagens.
            </p>
          </section>
        </div>
        <div class="grid gap-4 lg:grid-cols-2">
          <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
            <h3 class="mb-5 flex items-center gap-2 font-medium">
              <span class="i-lucide-chart-pie size-4 text-n-brand" />
              Entregas por categoria
            </h3>
            <div class="space-y-4">
              <div
                v-for="(count, category) in number.categories"
                :key="category"
              >
                <div class="mb-2 flex justify-between gap-3 text-sm">
                  <span>
                    {{ categories[category] || category }}
                  </span>
                  <span class="font-medium tabular-nums">
                    {{ usageCount(count) }}
                  </span>
                </div>
                <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
                  <div
                    class="h-full rounded-full bg-n-brand/70"
                    :style="{
                      width: `${(count / Math.max(1, number.delivered)) * 100}%`,
                    }"
                  />
                </div>
              </div>
            </div>
            <p
              v-if="!number.delivered"
              class="py-5 text-center text-sm text-n-slate-11"
            >
              Sem entregas registradas neste mês.
            </p>
          </section>
          <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
            <h3 class="mb-5 flex items-center gap-2 font-medium">
              <span class="i-lucide-chart-no-axes-column size-4 text-n-brand" />
              Entregas por dia
            </h3>
            <div class="max-h-64 overflow-y-auto pr-2 space-y-3">
              <div
                v-for="[day, count] in daily"
                :key="day"
                class="flex items-center gap-3 text-xs"
              >
                <span class="w-20 shrink-0 text-n-slate-11">
                  {{ day.split('-').reverse().join('/') }}
                </span>
                <div
                  class="h-2 flex-1 overflow-hidden rounded-full bg-n-alpha-2"
                >
                  <div
                    class="h-full rounded-full bg-n-brand"
                    :style="{ width: `${(count / maxDaily) * 100}%` }"
                  />
                </div>
                <span class="min-w-12 text-right font-medium tabular-nums">
                  {{ usageCount(count) }}
                </span>
              </div>
            </div>
            <p
              v-if="!daily.length"
              class="py-5 text-center text-sm text-n-slate-11"
            >
              Sem entregas registradas neste mês.
            </p>
          </section>
        </div>
      </template>
      <details
        v-if="accountOverview && report.numbers.length"
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-5"
      >
        <summary class="cursor-pointer font-medium">
          Consolidado por conta WhatsApp (WABA)
        </summary>
        <div class="mt-4 overflow-x-auto">
          <table class="w-full text-left text-sm">
            <thead class="text-xs text-n-slate-11">
              <tr>
                <th class="p-3">WABA</th>
                <th class="p-3">Números</th>
                <th class="p-3">Entregues</th>
                <th class="p-3">Com cobrança Meta</th>
                <th class="p-3">Estimativa parcial</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="waba in report.wabas"
                :key="waba.waba_id"
                class="border-t border-n-weak"
              >
                <td class="p-3 font-mono text-xs">{{ waba.waba_id }}</td>
                <td class="p-3 tabular-nums">{{ usageCount(waba.numbers) }}</td>
                <td class="p-3 tabular-nums">
                  {{ usageCount(waba.delivered) }}
                </td>
                <td class="p-3 tabular-nums">
                  {{ usageCount(waba.billable) }}
                </td>
                <td class="p-3 tabular-nums">
                  {{ money(waba.estimated_cost_brl)
                  }}<span
                    v-if="waba.unpriced"
                    class="mt-1 block text-xs text-n-slate-11"
                  >
                    {{ usageCount(waba.unpriced) }} sem tarifa aplicável
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </template>
    <details
      class="rounded-xl border border-n-weak p-4 text-sm text-n-slate-11"
    >
      <summary class="cursor-pointer font-medium">
        Como interpretar os valores
      </summary>
      <p class="mb-0 mt-3">
        A estimativa usa as tarifas em reais informadas por você. A meta mensal
        é uma referência de planejamento, não uma franquia oficial confirmada. O
        indicador de cobrança recebido da Meta prevalece. Este relatório não
        substitui a fatura, não bloqueia mensagens e usa sempre o horário de São
        Paulo.
      </p>
      <a
        href="https://developers.facebook.com/documentation/business-messaging/whatsapp/pricing/non-template-messages/"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-2 inline-flex text-n-brand underline"
      >
        Consultar preços oficiais
      </a>
    </details>
    <Dialog
      ref="settingsDialogRef"
      title="Configurar estimativa"
      :description="
        settingsTarget ? `${settingsTarget.name} · ${settingsTarget.phone}` : ''
      "
      width="2xl"
      sticky-header-footer
      overflow-y-auto
      :show-confirm-button="false"
      :show-cancel-button="false"
      :close-on-click-outside="!saving"
      @confirm="saveSettings"
      @close="showSettings = false"
    >
      <div v-if="showSettings" class="space-y-5">
        <p
          v-if="settingsError"
          role="alert"
          class="rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
        >
          {{ settingsError }}
        </p>
        <div class="flex items-start gap-3 rounded-xl bg-n-alpha-1 p-4">
          <span
            class="i-lucide-calculator mt-0.5 size-5 shrink-0 text-n-brand"
          />
          <p class="text-sm text-n-slate-11">
            Defina sua meta e as tarifas médias por mensagem. Alterar os
            parâmetros recalcula também os meses anteriores. Todos os períodos
            usam o horário de São Paulo.
          </p>
        </div>
        <section class="rounded-xl border border-n-weak p-4">
          <Input
            :model-value="draft.monthly_allowance"
            label="Meta mensal por número"
            inputmode="numeric"
            :disabled="saving"
            @update:model-value="
              draft.monthly_allowance = maskUsageCount($event)
            "
            @input="$event.target.value = draft.monthly_allowance"
          />
          <p class="mt-2 text-xs text-n-slate-11">
            De 0 a 100.000.000 mensagens. Serve apenas para acompanhar seu
            planejamento.
          </p>
        </section>
        <section>
          <h3 class="mb-1 text-sm font-medium text-n-slate-12">
            Tarifas por mensagem
          </h3>
          <p class="mb-4 text-xs text-n-slate-11">
            Valores em reais, com até 6 casas decimais. Ex.: R$ 0,035. Deixe
            vazio quando não houver tarifa; zero significa custo zero.
          </p>
          <div class="grid gap-4 sm:grid-cols-2">
            <Input
              v-for="category in [
                'service',
                'utility',
                'authentication',
                'marketing',
              ]"
              :key="category"
              :model-value="draft.rates[category]"
              :label="`${categories[category]} · R$ por mensagem`"
              placeholder="Sem tarifa configurada"
              inputmode="decimal"
              :disabled="saving"
              @update:model-value="
                draft.rates[category] = maskUsageRate($event)
              "
              @input="$event.target.value = draft.rates[category]"
              @blur="
                draft.rates[category] = formatUsageRate(
                  parseUsageRate(draft.rates[category])
                )
              "
            />
          </div>
        </section>
      </div>
      <template #footer>
        <div class="flex flex-wrap justify-end gap-3">
          <Button
            label="Cancelar"
            type="button"
            variant="outline"
            color="slate"
            :disabled="saving"
            @click="settingsDialogRef?.close()"
          />
          <Button
            label="Salvar parâmetros"
            type="button"
            :is-loading="saving"
            :disabled="saving"
            @click="saveSettings"
          />
        </div>
      </template>
    </Dialog>
  </section>
</template>
